import type { AxiosInstance, AxiosResponse } from 'axios';

import instantSurveyApi from './api';
import { createApiRequestConfig, transformApiResponseData } from '../utilities/api_case';
import { transformQuestion } from './utilities/helpers';
import type { Question, ScreenerQuestionBank } from './types';

const axios = instantSurveyApi as AxiosInstance;

type ScreenerQuestionBankResponse = Omit<
  ScreenerQuestionBank,
  'createdAt' | 'questions' | 'updatedAt'
> & {
  created_at: ScreenerQuestionBank['createdAt'];
  questions: Parameters<typeof transformQuestion>[0][];
  updated_at: ScreenerQuestionBank['updatedAt'];
};

interface ScreenerQuestionBanks {
  count: number;
  next: null | string;
  previous: null | string;
  results: ScreenerQuestionBank[];
}

interface ScreenerQuestionBanksResponse extends Omit<ScreenerQuestionBanks, 'results'> {
  results: ScreenerQuestionBankResponse[];
}

/**
 * Read screener question banks for the Numerator screener library.
 */
export const readScreenerQuestionBanks = async ({
  limit,
  name,
  offset,
  ordering,
  signal,
}: {
  limit?: number;
  name?: string;
  offset?: number;
  ordering?: string;
  signal?: AbortSignal;
} = {}): Promise<ScreenerQuestionBanks> => {
  return axios(
    createApiRequestConfig({
      method: 'get',
      params: { limit, name, offset, ordering },
      signal,
      url: '/instantsurvey/screener_questionbank',
    })
  ).then(({ data }: AxiosResponse<ScreenerQuestionBanksResponse>) => {
    const { results, ...pagination } = data;

    return {
      ...transformApiResponseData(pagination),
      results: results.map(({ questions, ...questionBank }) => ({
        ...transformApiResponseData(questionBank),
        questions: questions.map((question) => transformQuestion(question) as Question),
      })),
    } as ScreenerQuestionBanks;
  });
};
